import { Dialog } from "@headlessui/react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Button, CloseIcon, Header, IconButton, Paragraph, TrashCanIcon } from "@datagame/ui";

import { Game } from "../../../../gql/graphql";
import { GAME_ALL } from "../../../../graphql/queries/GameQueries";
import { GAME_DELETE } from "../../../../graphql/mutations/GameMutation";
import { useProject } from "../../../../contexts/project/ProjectContext";

import styles from "./DeleteGameModal.module.scss";

interface DeleteGameModalProps {
  game: Game;
  onClose: () => void;
}

export const DeleteGameModal = ({ game, onClose }: DeleteGameModalProps) => {
  const { project: { name: projectName, id: projectId } = {} } = useProject();
  const { t: tCommon } = useTranslation("common");
  const { t: translation } = useTranslation("project");
  const t = (key: string) => translation(`addGames.${key}`);

  const [deleteGame] = useMutation(GAME_DELETE, {
    variables: { gameId: game.id },
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
  });

  const handleDelete = async () => {
    await deleteGame();
    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <div className={styles.overlay} />
      <Dialog.Panel>
        <div className={styles.modal}>
          <div className={styles.header}>
            <Header variant="h5">{t("delete.label")}</Header>

            <IconButton size="small" onClick={onClose} className={styles.close} icon={<CloseIcon />} />
          </div>

          <Paragraph className={styles.content} color="secondary">
            {t("delete.description1")}
            <span className={styles.bold}>{game.name}</span>
            {t("delete.description2")}
            <span className={styles.bold}>{projectName}</span>?
          </Paragraph>

          <div className={styles.buttons}>
            <Button icon={<CloseIcon />} kind="outline" iconSide="left" size="small" onClick={onClose} color="black">
              {tCommon("Cancel")}
            </Button>
            <Button icon={<TrashCanIcon />} color="red" iconSide="left" size="small" onClick={handleDelete}>
              {tCommon("Delete")}
            </Button>
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
